import React from 'react';

/**
 * PageHeader - A reusable sticky page header component
 * 
 * @param {string} title - Main title text
 * @param {string} icon - Optional emoji/icon to display before title
 * @param {string} subtitle - Optional subtitle/description
 * @param {React.ReactNode} leftAction - Optional left action (e.g., back button)
 * @param {React.ReactNode} rightAction - Optional right action(s)
 * @param {boolean} transparent - Use transparent/blur background (default: false)
 * @param {string} className - Additional CSS classes
 */
const PageHeader = ({
  title,
  icon,
  subtitle,
  leftAction,
  rightAction,
  transparent = false,
  className = ''
}) => {
  const bgClasses = transparent
    ? 'bg-white/80 backdrop-blur-sm'
    : 'bg-white';

  return (
    <header 
      className={`${bgClasses} shadow-sm sticky top-0 z-40 ${className}`}
      data-testid="page-header"
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {leftAction && (
              <div data-testid="page-header-left-action">
                {leftAction}
              </div>
            )}
            {icon && <span className="text-3xl">{icon}</span>}
            <div>
              <h1 
                className="text-2xl font-bold text-amber-900"
                data-testid="page-header-title"
              >
                {title}
              </h1>
              {subtitle && (
                <p className="text-sm text-gray-500" data-testid="page-header-subtitle">
                  {subtitle}
                </p>
              )}
            </div>
          </div>
          {rightAction && (
            <div data-testid="page-header-right-action">
              {rightAction}
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default PageHeader;
